import React, { useState } from 'react';
import { Search } from 'lucide-react';
import ElementCategory from '../ElementLibrary/ElementCategory';
import { ElementType } from '../../types/element.types';

/**
 * Right Panel Component
 * Displays the element library with categorized elements
 */
const RightPanel: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // Element categories with their elements
  const categories = [
    {
      name: 'Layout',
      elements: [
        { type: 'columnLayout' as ElementType, label: '2/3/4 Columns', icon: '⚏' },
        { type: 'spacer' as ElementType, label: 'Spacer', icon: '↕' },
        { type: 'divider' as ElementType, label: 'Divider', icon: '—' },
        { type: 'contentBox' as ElementType, label: 'Content Box', icon: '▢' },
      ],
    },
    {
      name: 'Typography',
      elements: [
        { type: 'heading' as ElementType, label: 'Heading', icon: 'H' },
        { type: 'text' as ElementType, label: 'Text', icon: 'T' },
        { type: 'bulletedList' as ElementType, label: 'Bulleted List', icon: '•' },
      ],
    },
    {
      name: 'Media',
      elements: [
        { type: 'image' as ElementType, label: 'Image', icon: '🖼' },
        { type: 'video' as ElementType, label: 'Video', icon: '▶' },
        { type: 'carousel' as ElementType, label: 'Carousel', icon: '⟳' },
        { type: 'icon' as ElementType, label: 'Icon', icon: '★' },
      ],
    },
    {
      name: 'Interactive',
      elements: [
        { type: 'button' as ElementType, label: 'Button', icon: '▭' },
        { type: 'input' as ElementType, label: 'Input', icon: '⎵' },
        { type: 'textarea' as ElementType, label: 'Textarea', icon: '▭' },
        { type: 'checkbox' as ElementType, label: 'Checkbox', icon: '☑' },
        { type: 'select' as ElementType, label: 'Select', icon: '▼' },
        { type: 'accordion' as ElementType, label: 'Accordion', icon: '≡' },
        { type: 'tabs' as ElementType, label: 'Tabs', icon: '⊟' },
      ],
    },
    {
      name: 'Advanced',
      elements: [
        { type: 'customHtml' as ElementType, label: 'Custom HTML', icon: '<>' },
      ],
    },
  ];

  // Filter elements based on search query
  const filteredCategories = categories
    .map((category) => ({
      ...category,
      elements: category.elements.filter((element) =>
        element.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.elements.length > 0);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">Elements</h2>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search elements..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg 
                     bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#003C07] 
                     focus:border-[#003C07] transition-all placeholder:text-gray-400"
          />
        </div>
      </div>

      {/* Element Categories */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredCategories.length === 0 ? (
          <div className="text-center text-gray-500 text-sm mt-8">
            No elements found
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCategories.map((category) => (
              <ElementCategory
                key={category.name}
                name={category.name}
                elements={category.elements}
              />
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-600">
          💡 Drag elements onto the canvas to add them to your funnel
        </p>
      </div>
    </div>
  );
};

export default RightPanel;

